import React, { useState } from 'react';
import Button from './Button';
import Icon from '../AppIcon';

const DeleteConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  itemName, 
  itemType = 'item',
  warningMessage,
  additionalInfo 
}) => {
  const [confirmationText, setConfirmationText] = useState('');
  const [isChecked, setIsChecked] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen || !itemName) return null;

  const isDeleteEnabled = 
    confirmationText?.trim()?.toLowerCase() === itemName?.toLowerCase() && 
    isChecked;

  const handleDelete = async () => {
    if (!isDeleteEnabled) return;
    
    setIsDeleting(true);
    try {
      await onConfirm();
      // Reset state after successful deletion
      setConfirmationText('');
      setIsChecked(false);
      setIsDeleting(false);
      // Close modal after successful deletion
      onClose();
    } catch (error) {
      console.error('Delete operation failed:', error);
      setIsDeleting(false);
      // Keep modal open on error so user can retry
    }
  };

  const handleClose = () => {
    setConfirmationText('');
    setIsChecked(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} className="text-destructive" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">Delete {itemType}</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Warning Message */}
          <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4">
            <p className="text-sm text-foreground">
              {warningMessage || (
                <>
                  This action <strong>cannot be undone</strong>. This will permanently delete
                  <strong className="text-destructive"> "{itemName}"</strong>
                  {additionalInfo && ` ${additionalInfo}`}.
                </>
              )}
            </p>
          </div>

          {/* Confirmation Step 1: Type item name */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Type <span className="font-semibold text-destructive">{itemName}</span> to confirm:
            </label>
            <input
              type="text"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e?.target?.value)}
              placeholder={`Enter ${itemType} name`}
              className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Confirmation Step 2: Checkbox */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="delete-confirm"
              checked={isChecked}
              onChange={(e) => setIsChecked(e?.target?.checked)}
              className="mt-1 w-4 h-4 rounded border-input text-destructive focus:ring-destructive"
            />
            <label htmlFor="delete-confirm" className="text-sm text-foreground cursor-pointer">
              I understand that this action is permanent and will delete all {itemType} data.
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!isDeleteEnabled || isDeleting}
            iconName={isDeleting ? "Loader2" : "Trash2"}
            className={isDeleting ? "animate-spin" : ""}
          >
            {isDeleting ? 'Deleting...' : `Delete ${itemType}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;